/**
 * ProfitabilityChart Component
 * Used in: Dashboard screen - displays Profitability Analysis (Monthly)
 */
import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { FinancialData, getDataByViewMode } from '../data/financialData';

interface ProfitabilityChartProps {
  viewMode?: 'monthly' | 'yearly';
  data: FinancialData[];
}

export const ProfitabilityChart: React.FC<ProfitabilityChartProps> = ({ viewMode: timeViewMode = 'monthly', data }) => {
  const [viewMode, setViewMode] = useState<'absolute' | 'margins'>('absolute');
  
  // Get data based on time view mode
  const chartData = useMemo(() => getDataByViewMode(timeViewMode, data), [timeViewMode, data]);
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Process data with margin calculations
  const processedChartData = useMemo(() => {
    return chartData.map(item => ({
      ...item,
      grossProfitMargin: item.totalIncome > 0 ? (item.grossProfit / item.totalIncome) * 100 : 0,
      netProfitMargin: item.totalIncome > 0 ? (item.netOrdinaryIncome / item.totalIncome) * 100 : 0,
    }));
  }, [chartData]);

  // Calculate mean values for the series
  const meanValues = useMemo(() => {
    if (viewMode === 'absolute') {
      const grossProfitSum = chartData.reduce((sum, item) => sum + item.grossProfit, 0);
      const netOrdinaryIncomeSum = chartData.reduce((sum, item) => sum + item.netOrdinaryIncome, 0);
      
      return {
        grossProfit: grossProfitSum / chartData.length,
        netOrdinaryIncome: netOrdinaryIncomeSum / chartData.length,
      };
    } else {
      const grossMarginSum = processedChartData.reduce((sum, item) => sum + item.grossProfitMargin, 0);
      const netMarginSum = processedChartData.reduce((sum, item) => sum + item.netProfitMargin, 0);
      
      return {
        grossProfit: grossMarginSum / processedChartData.length,
        netOrdinaryIncome: netMarginSum / processedChartData.length,
      };
    }
  }, [chartData, processedChartData, viewMode]);

  // Custom tick formatter for quarterly display
  const formatXAxisTick = (tickItem: string) => {
    // Only show labels for quarter-end months (Mar, Jun, Sep, Dec)
    const monthName = tickItem.split(' ')[0];
    const quarterEndMonths = ['Mar', 'Jun', 'Sep', 'Dec'];
    
    if (quarterEndMonths.includes(monthName)) {
      return tickItem;
    }
    return ''; // Return empty string for non-quarter-end months (will still show tick mark)
  };

  // Custom tick component to ensure all months get tick marks
  const CustomTick = (props: any) => {
    const { x, y, payload } = props;
    const monthName = payload.value.split(' ')[0];
    const quarterEndMonths = ['Mar', 'Jun', 'Sep', 'Dec'];
    const showLabel = quarterEndMonths.includes(monthName);
    
    return (
      <g transform={`translate(${x},${y})`}>
        {/* Always render the tick mark */}
        <line x1={0} y1={0} x2={0} y2={6} stroke="#6b7280" />
        {/* Only render text for quarter-end months */}
        {showLabel && (
          <text 
            x={0} 
            y={12} 
            dy={8} 
            textAnchor="end" 
            fill="#6b7280" 
            fontSize="11"
            transform="rotate(-45)"
          >
            {payload.value}
          </text>
        )}
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {viewMode === 'absolute' ? formatCurrency(entry.value) : formatPercentage(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Profitability Analysis ({timeViewMode === 'yearly' ? 'Yearly' : 'Monthly'})
        </h3>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('absolute')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'absolute' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Absolute Values
          </button>
          <button
            onClick={() => setViewMode('margins')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'margins' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Profit Margins
          </button>
        </div>
      </div>
      <div className="chart-container w-full overflow-x-auto">
        <ResponsiveContainer width="100%" height={450} minWidth={300}>
          <LineChart data={processedChartData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="month" 
            stroke="#6b7280"
            height={60}
            interval="preserveStartEnd"
            tick={<CustomTick />}
            axisLine={{ stroke: '#6b7280' }}
            tickLine={false}
          />
          <YAxis 
            yAxisId="currency"
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={viewMode === 'absolute' ? formatCurrency : formatPercentage}
          />
          <YAxis 
            yAxisId="means"
            orientation="right"
            stroke="#6b7280"
            fontSize={10}
            domain={['dataMin', 'dataMax']}
            tickFormatter={() => ''}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="2 2" yAxisId="currency" />
          
          {/* Mean reference lines */}
          <ReferenceLine
            y={meanValues.grossProfit}
            stroke="#10b981"
            strokeDasharray="5 5"
            strokeWidth={1.5}
            yAxisId="currency"
            label={{
              value: `μ ${viewMode === 'absolute' ? formatCurrency(meanValues.grossProfit) : formatPercentage(meanValues.grossProfit)}`,
              position: "right",
              offset: 5,
              style: { fontSize: '10px', fill: '#10b981', fontWeight: '600' }
            }}
          />
          <ReferenceLine
            y={meanValues.netOrdinaryIncome}
            stroke="#8b5cf6"
            strokeDasharray="5 5"
            strokeWidth={1.5}
            yAxisId="currency"
            label={{
              value: `μ ${viewMode === 'absolute' ? formatCurrency(meanValues.netOrdinaryIncome) : formatPercentage(meanValues.netOrdinaryIncome)}`,
              position: "right",
              offset: 5,
              style: { fontSize: '10px', fill: '#8b5cf6', fontWeight: '600' }
            }}
          />
          
          <Line 
            type="monotone" 
            dataKey={viewMode === 'absolute' ? 'grossProfit' : 'grossProfitMargin'}
            stroke="#10b981" 
            strokeWidth={3}
            name={viewMode === 'absolute' ? 'Gross Profit' : 'Gross Profit Margin'}
            dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
            yAxisId="currency"
          />
          <Line 
            type="monotone" 
            dataKey={viewMode === 'absolute' ? 'netOrdinaryIncome' : 'netProfitMargin'}
            stroke="#8b5cf6" 
            strokeWidth={3}
            name={viewMode === 'absolute' ? 'Net Profit' : 'Net Profit Margin'}
            dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#8b5cf6', strokeWidth: 2 }}
            yAxisId="currency"
          />
        </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};